import { makeStyles, Theme } from '@material-ui/core';
import React, { useState } from 'react';

import { FONT_HEAVY, FONT_MED } from '../../styles/fonts';

export interface MilestoneItem {
  title: string;
  date?: string;
  isComplete: boolean;
  isOngoing: boolean;
}

interface MilestonesTimelineProps {
  items: ReadonlyArray<MilestoneItem>;
  dataTestId?: string;
  hidePrevious?: boolean;
  onToggleHidePrevious?: () => void;
}

type MilestoneState = 'complete' | 'current' | 'future';

function resolveCurrentIndex(items: ReadonlyArray<MilestoneItem>): number {
  const ongoingIndex = items.findIndex((item) => item.isOngoing);
  if (ongoingIndex !== -1) return ongoingIndex;
  return items.reduce((latest, item, idx) => (item.isComplete ? idx : latest), -1);
}

function resolveState(index: number, currentIndex: number): MilestoneState {
  if (index === currentIndex) return 'current';
  if (index < currentIndex) return 'complete';
  return 'future';
}

export default function MilestonesTimeline({
  items,
  dataTestId,
  hidePrevious: hidePreviousProp,
  onToggleHidePrevious,
}: MilestonesTimelineProps) {
  const classes = useStyles();
  const [internalHidePrevious, setInternalHidePrevious] = useState(false);

  const isControlled = hidePreviousProp !== undefined;
  const hidePrevious = isControlled ? hidePreviousProp : internalHidePrevious;
  const toggleHidePrevious = () => {
    if (isControlled) {
      onToggleHidePrevious?.();
    } else {
      setInternalHidePrevious((prev) => !prev);
    }
  };

  const currentIndex = resolveCurrentIndex(items);
  const hasPrevious = currentIndex > 0;
  const decoratedItems = items.map((item, idx) => ({ item, state: resolveState(idx, currentIndex) }));
  const visibleItems = hidePrevious ? decoratedItems.filter(({ state }) => state !== 'complete') : decoratedItems;

  return (
    <div className={classes.root} data-testid={dataTestId}>
      {hasPrevious && (
        <button
          type="button"
          className={classes.toggle}
          onClick={toggleHidePrevious}
          data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
        >
          {hidePrevious ? 'Show Previous Milestones' : 'Hide Previous Milestones'}
        </button>
      )}
      <div className={classes.list}>
        {visibleItems.map(({ item, state }, idx) => {
          const isLast = idx === visibleItems.length - 1;
          return (
            <div className={classes.row} key={`${item.title}-${idx}`}>
              <div className={classes.dotColumn}>
                <span className={`${classes.dot} ${classes[`dot_${state}`]}`} />
                {!isLast && <span className={classes.line} />}
              </div>
              <div className={classes.content}>
                <div className={`${classes.title} ${classes[`title_${state}`]}`}>{item.title}</div>
                {item.date && state !== 'future' && (
                  <div className={`${classes.date} ${state === 'current' ? classes.date_current : ''}`}>
                    {item.date}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DOT_SIZE = 8;
const COLUMN_WIDTH = 16;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  toggle: {
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    textAlign: 'left',
    ...FONT_MED,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    gap: 12,
    paddingBottom: 16,
    '&:last-child': { paddingBottom: 0 },
  },
  dotColumn: {
    position: 'relative',
    width: COLUMN_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: '50%',
    marginTop: 6,
    flexShrink: 0,
  },
  dot_complete: { backgroundColor: theme.palette.text.disabled },
  dot_current: { backgroundColor: theme.palette.primary.main },
  dot_future: { backgroundColor: theme.palette.divider },
  line: {
    width: 1,
    flex: 1,
    backgroundColor: theme.palette.divider,
    marginTop: 2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.25px',
  },
  title_complete: {
    color: theme.palette.text.primary,
    ...FONT_MED,
  },
  title_current: {
    color: theme.palette.text.primary,
    ...FONT_HEAVY,
  },
  title_future: {
    color: theme.palette.text.secondary,
    ...FONT_MED,
  },
  date: {
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: theme.palette.text.secondary,
    ...FONT_MED,
  },
  date_current: {
    color: theme.palette.text.secondary,
    ...FONT_HEAVY,
  },
}));
